import { useCallback } from "react";
import { createFileRoute, Outlet, redirect } from "@tanstack/react-router";
import type { LiveEvent } from "@tunic/shared";
import { meQuery } from "@/api/auth";
import { ApiError } from "@/api/client";
import { useLiveEvents } from "@/api/live";
import { useUndo } from "@/api/projects";
import { AppShell } from "@/components/shell/AppShell";
import { DropAnywhere } from "@/components/intake/DropAnywhere";
import { toastIntakeOutcome } from "@/components/intake/intakeToasts";
import { useUndoToast } from "@/hooks/useUndoToast";

export const Route = createFileRoute("/_app")({
  beforeLoad: async ({ context, location }) => {
    try {
      await context.queryClient.ensureQueryData(meQuery);
    } catch (err) {
      // Only a real 401 means "not signed in"; a network blip (API restarting) must not log people out.
      if (err instanceof ApiError && err.status === 401) throw redirect({ to: "/login", search: { redirect: location.href } });
    }
  },
  component: AppLayout,
});

function AppLayout() {
  const undo = useUndo();
  const undoToast = useUndoToast();
  const onIntakeProcessed = useCallback(
    (ev: Extract<LiveEvent, { event: "intake_processed" }>) => {
      toastIntakeOutcome(ev, (eventId) => undoToast.run(undo.mutateAsync(eventId), "Filing undone"));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  useLiveEvents({ onIntakeProcessed });
  return (
    <AppShell>
      <DropAnywhere />
      <Outlet />
    </AppShell>
  );
}
